import React, { useState } from 'react'
import {
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Switch,
    Typography,
    Box,
} from '@mui/material'
import MicNoneIcon from '@mui/icons-material/MicNone'
import OndemandVideoOutlinedIcon from '@mui/icons-material/OndemandVideoOutlined'
import ScreenShareOutlinedIcon from '@mui/icons-material/ScreenShareOutlined'

import {
    VOICE_SOURCES,
    isCallSource,
    isCallCaptureAvailable,
    platformHint,
    readVoiceSourcePreference,
    saveVoiceSourcePreference,
} from '../../utils/voiceSource'

const SOURCE_ITEMS = [
    {
        source: VOICE_SOURCES.MIC,
        label: 'Microphone',
        hint: 'Dictate: every pause adds a statement',
        Icon: MicNoneIcon,
    },
    {
        source: VOICE_SOURCES.TAB,
        label: 'Call or video in a browser tab',
        hint: 'Meet, Zoom, YouTube… every pause adds what was said',
        Icon: OndemandVideoOutlinedIcon,
    },
    {
        source: VOICE_SOURCES.SCREEN,
        label: 'Computer audio (screen share)',
        hint: 'Desktop apps like Zoom, via system audio',
        Icon: ScreenShareOutlinedIcon,
    },
]

/**
 * Picks where voice input listens. `onSelect({ source, includeMic })` is
 * called synchronously after the menu closes, so the caller can reach the
 * browser's share picker while the click still counts as a user gesture.
 * `keepFocus` stops the menu from stealing focus (needed inside the import
 * dialog, whose text field collapses on blur).
 */
export default function VoiceSourceMenu({
    anchorEl,
    open,
    onClose,
    onSelect,
    keepFocus = false,
}) {
    const preference = readVoiceSourcePreference()
    const [includeMic, setIncludeMic] = useState(preference.includeMic)
    const [hoveredSource, setHoveredSource] = useState(null)

    const callCaptureAvailable = isCallCaptureAvailable()
    const hintSource =
        hoveredSource ||
        (isCallSource(preference.source)
            ? preference.source
            : VOICE_SOURCES.TAB)

    const choose = source => {
        const selection = { source, includeMic }
        saveVoiceSourcePreference(selection)
        onClose()
        onSelect(selection)
    }

    const focusProps = keepFocus
        ? {
              autoFocus: false,
              disableAutoFocusItem: true,
              MenuListProps: {
                  onMouseDown: event => event.preventDefault(),
              },
          }
        : {}

    return (
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            {...focusProps}
        >
            {SOURCE_ITEMS.map(({ source, label, hint, Icon }) => {
                const disabled = isCallSource(source) && !callCaptureAvailable
                return (
                    <MenuItem
                        key={source}
                        data-testid={`voice-source-${source}`}
                        disabled={disabled}
                        onClick={() => choose(source)}
                        onMouseEnter={() => setHoveredSource(source)}
                        sx={{ minWidth: 300 }}
                    >
                        <ListItemIcon>
                            <Icon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={label} secondary={hint} />
                    </MenuItem>
                )
            })}
            <Divider />
            <MenuItem
                data-testid="voice-include-mic"
                disabled={!callCaptureAvailable}
                onClick={event => {
                    event.stopPropagation()
                    setIncludeMic(value => !value)
                }}
            >
                <ListItemIcon>
                    <Switch
                        size="small"
                        edge="start"
                        checked={includeMic}
                        tabIndex={-1}
                        inputProps={{ 'aria-label': 'Include my microphone' }}
                    />
                </ListItemIcon>
                <ListItemText
                    primary="Include my microphone"
                    secondary="For calls: your side is tagged speaker: me"
                />
            </MenuItem>
            <Box sx={{ px: 2, pt: 1, pb: 0.5, maxWidth: 340 }}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                >
                    {platformHint(hintSource)}
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                >
                    Let participants know the call is transcribed. Use
                    headphones to avoid echo.
                </Typography>
            </Box>
        </Menu>
    )
}
